/**
 * The board's geometry, pure (no Solid, no DOM), so it is the same on every page and tested
 * alone: where each item sits ({@link boardOf}: its own `board` place, else one made from its
 * class-diagram place, else a free spot on a grid below the placed ones), what a frame holds,
 * what a point or a rectangle touches, snapping to the other items' edges, aligning and
 * spreading, and the places a move or a resize gives.
 *
 * Items are nodes; frames are groups that have a `board` rectangle (a group without one is a
 * plain group: its members are selected and moved together, it draws nothing).
 */
import type { BoardPlace } from "@protocol/BoardPlace";
import type { Graph } from "@protocol/Graph";
import type { Group } from "@protocol/Group";
import type { Node } from "@protocol/Node";

/** A rectangle in board units. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A point in board units. */
export interface Point {
  x: number;
  y: number;
}

/** One node on the board, with the place it is drawn at. */
export interface BoardItem {
  id: string;
  node: Node;
  place: BoardPlace;
  /** It has no place of its own yet (drawn where {@link boardOf} put it). */
  placed: boolean;
}

/** One frame (a group with a rectangle), with its depth among frames (0: top level). */
export interface BoardFrame {
  id: string;
  group: Group;
  place: BoardPlace;
  depth: number;
}

/** What the board draws: frames from the outermost in, then the items, lowest `z` first. */
export interface BoardContent {
  frames: BoardFrame[];
  items: BoardItem[];
  /** Every frame and item by id. */
  places: Map<string, BoardPlace>;
}

/** The size a new item of a kind starts with (Miro's: a square note, a wide text...; UI elements their usual size). */
export function defaultSize(kind: string): { w: number; h: number } {
  if (/note/.test(kind)) return { w: 200, h: 200 };
  if (/button/.test(kind)) return { w: 160, h: 44 };
  if (/input/.test(kind)) return { w: 280, h: 44 };
  if (/checkbox/.test(kind)) return { w: 200, h: 28 };
  if (/list/.test(kind)) return { w: 240, h: 120 };
  if (/text/.test(kind)) return { w: 280, h: 60 };
  if (/image/.test(kind)) return { w: 320, h: 220 };
  return { w: 220, h: 120 };
}

/** Room between the items {@link boardOf} lays out on a grid. */
const GRID_GAP = 40;
/** How many items a row of the grid holds. */
const GRID_COLUMNS = 6;

/**
 * Where every node of `graph` sits on the board and which groups are frames. A node without a
 * place of its own is put at its class-diagram place when it has one (a board grown from a
 * diagram keeps its picture), else in rows below everything placed.
 */
export function boardOf(graph: Graph): BoardContent {
  const places = new Map<string, BoardPlace>();
  const items: BoardItem[] = [];
  let bottom = 0;
  let left = Infinity;
  const waiting: Node[] = [];
  for (const node of graph.nodes) {
    const size = defaultSize(node.kind);
    const place = node.board ?? (node.diagram && node.diagram.length === 2 ? { x: node.diagram[0]!, y: node.diagram[1]!, ...size } : null);
    if (!place) {
      waiting.push(node);
      continue;
    }
    items.push({ id: node.id, node, place, placed: !node.board });
    bottom = Math.max(bottom, place.y + place.h);
    left = Math.min(left, place.x);
  }
  const frames = framesOf(graph.groups);
  for (const f of frames) bottom = Math.max(bottom, f.place.y + f.place.h), (left = Math.min(left, f.place.x));
  if (!Number.isFinite(left)) left = 0;
  const top = items.length || frames.length ? bottom + GRID_GAP * 2 : 0;
  waiting.forEach((node, i) => {
    const size = defaultSize(node.kind);
    const cell = 240 + GRID_GAP;
    const place = { x: left + (i % GRID_COLUMNS) * cell, y: top + Math.floor(i / GRID_COLUMNS) * cell, ...size };
    items.push({ id: node.id, node, place, placed: true });
  });
  layOut(graph, frames, items);
  items.sort((a, b) => (a.place.z ?? 0) - (b.place.z ?? 0));
  for (const item of items) places.set(item.id, item.place);
  for (const frame of frames) places.set(frame.id, frame.place);
  return { frames, items, places };
}

/**
 * Auto layout (Figma's): a frame whose `style.layout` is `row` or `column` places what it holds
 * itself, one after the other in the order of their own places (so dragging one past another
 * swaps them), `gap` apart, `padding` in from its edges, and across lined up by `align`
 * (`start`, `center`, `end`, or `stretch`: as wide as the frame). Outer frames go first; a frame
 * moved by its parent's layout takes what it holds along. Places are changed in `items` and
 * `frames`; what is kept in the graph stays as it was.
 */
function layOut(graph: Graph, frames: BoardFrame[], items: BoardItem[]): void {
  const frameById = new Map(frames.map((f) => [f.id, f]));
  const itemById = new Map(items.map((i) => [i.id, i]));
  /** Moves a frame's contents by (`dx`, `dy`). */
  const carry = (frame: string, dx: number, dy: number) => {
    if (!dx && !dy) return;
    const { nodes, frames: inner } = contentsOf(graph, frame);
    for (const id of nodes) {
      const item = itemById.get(id);
      if (item) item.place = { ...item.place, x: item.place.x + dx, y: item.place.y + dy };
    }
    for (const id of inner) {
      const f = frameById.get(id);
      if (f) f.place = { ...f.place, x: f.place.x + dx, y: f.place.y + dy };
    }
  };
  for (const frame of frames) {
    const style = frame.group.style ?? {};
    const row = style.layout === "row";
    if (!row && style.layout !== "column") continue;
    const [main, cross, mainSize, crossSize] = row ? (["x", "y", "w", "h"] as const) : (["y", "x", "h", "w"] as const);
    const pad = Math.max(0, Number(style.padding) || 0);
    const gap = Math.max(0, Number(style.gap) || 0);
    const children: { id: string; place: BoardPlace; set: (p: BoardPlace) => void; frame: boolean }[] = [
      ...items.filter((i) => i.node.group === frame.id).map((i) => ({ id: i.id, place: i.place, set: (p: BoardPlace) => void (i.place = p), frame: false })),
      ...frames.filter((f) => f.group.parent === frame.id).map((f) => ({ id: f.id, place: f.place, set: (p: BoardPlace) => void (f.place = p), frame: true })),
    ];
    children.sort((a, b) => a.place[main] - b.place[main] || a.place[cross] - b.place[cross] || (a.id < b.id ? -1 : 1));
    const box = frame.place;
    let at = box[main] + pad;
    for (const child of children) {
      const p: BoardPlace = { ...child.place, [main]: at };
      const room = box[crossSize] - pad * 2;
      if (style.align === "stretch") (p[cross] = box[cross] + pad), (p[crossSize] = Math.max(1, room));
      else if (style.align === "center") p[cross] = box[cross] + pad + (room - p[crossSize]) / 2;
      else if (style.align === "end") p[cross] = box[cross] + box[crossSize] - pad - p[crossSize];
      else p[cross] = box[cross] + pad;
      if (child.frame) carry(child.id, p.x - child.place.x, p.y - child.place.y);
      child.set(p);
      at += p[mainSize] + gap;
    }
  }
}

/** The groups that are frames, outermost first, with their depth. */
function framesOf(groups: Group[]): BoardFrame[] {
  const byId = new Map(groups.map((g) => [g.id, g]));
  const depthOf = (group: Group): number => {
    let depth = 0;
    const seen = new Set<string>();
    for (let parent = group.parent; parent && !seen.has(parent); parent = byId.get(parent)?.parent ?? null) {
      seen.add(parent);
      if (byId.get(parent)?.board) depth++;
    }
    return depth;
  };
  return groups
    .filter((g): g is Group & { board: BoardPlace } => !!g.board)
    .map((group) => ({ id: group.id, group, place: group.board, depth: depthOf(group) }))
    .sort((a, b) => a.depth - b.depth || (a.place.z ?? 0) - (b.place.z ?? 0));
}

/**
 * Everything a frame (or a plain group) carries along when it moves: the nodes in it or in a
 * group inside it, and the frames inside it, by id.
 */
export function contentsOf(graph: Graph, group: string): { nodes: string[]; frames: string[] } {
  const inside = new Set([group]);
  // Groups inside groups, in any order: go round until nothing more joins.
  for (let grew = true; grew; ) {
    grew = false;
    for (const g of graph.groups) if (g.parent && inside.has(g.parent) && !inside.has(g.id)) inside.add(g.id), (grew = true);
  }
  return {
    nodes: graph.nodes.filter((n) => n.group && inside.has(n.group)).map((n) => n.id),
    frames: graph.groups.filter((g) => g.id !== group && inside.has(g.id) && g.board).map((g) => g.id),
  };
}

/** The ids `ids` move along with: themselves, and what the frames among them hold. */
export function carried(graph: Graph, ids: string[]): string[] {
  const all = new Set(ids);
  for (const id of ids) {
    if (!graph.groups.some((g) => g.id === id)) continue;
    const { nodes, frames } = contentsOf(graph, id);
    for (const x of [...nodes, ...frames]) all.add(x);
  }
  return [...all];
}

/** `p` in the item's own frame (its rotation undone around its centre). */
function unrotated(place: BoardPlace, p: Point): Point {
  const angle = -((place.rotate ?? 0) * Math.PI) / 180;
  if (!angle) return p;
  const cx = place.x + place.w / 2;
  const cy = place.y + place.h / 2;
  const [dx, dy] = [p.x - cx, p.y - cy];
  return { x: cx + dx * Math.cos(angle) - dy * Math.sin(angle), y: cy + dx * Math.sin(angle) + dy * Math.cos(angle) };
}

/** Whether `p` is inside `place` (its rotation counted). */
export function contains(place: BoardPlace, p: Point): boolean {
  const q = unrotated(place, p);
  return q.x >= place.x && q.x <= place.x + place.w && q.y >= place.y && q.y <= place.y + place.h;
}

/** The topmost item under `p`, else the innermost frame whose title bar or empty inside is under it. */
export function hitAt(content: BoardContent, p: Point): { type: "item"; id: string } | { type: "frame"; id: string } | null {
  for (let i = content.items.length - 1; i >= 0; i--) if (contains(content.items[i]!.place, p)) return { type: "item", id: content.items[i]!.id };
  for (let i = content.frames.length - 1; i >= 0; i--) if (contains(content.frames[i]!.place, p)) return { type: "frame", id: content.frames[i]!.id };
  return null;
}

/** The innermost frame holding the centre of `rect`, leaving out `except` and what is inside them. */
export function frameAround(content: BoardContent, rect: Rect, except: Set<string> = new Set()): string | null {
  const centre = { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 };
  for (let i = content.frames.length - 1; i >= 0; i--) {
    const f = content.frames[i]!;
    if (!except.has(f.id) && contains(f.place, centre)) return f.id;
  }
  return null;
}

/** The ids of the items (and, with `frames`, the frames) wholly inside `rect` (a marquee). */
export function inside(content: BoardContent, rect: Rect, frames = false): string[] {
  const r = normal(rect);
  const within = (p: BoardPlace) => p.x >= r.x && p.y >= r.y && p.x + p.w <= r.x + r.w && p.y + p.h <= r.y + r.h;
  return [...(frames ? content.frames.filter((f) => within(f.place)).map((f) => f.id) : []), ...content.items.filter((i) => within(i.place)).map((i) => i.id)];
}

/** `rect` with a positive width and height (a marquee dragged up or left). */
export function normal(rect: Rect): Rect {
  return { x: Math.min(rect.x, rect.x + rect.w), y: Math.min(rect.y, rect.y + rect.h), w: Math.abs(rect.w), h: Math.abs(rect.h) };
}

/** The smallest rectangle around `places` (`null` for none). */
export function bounds(places: Rect[]): Rect | null {
  if (!places.length) return null;
  const x = Math.min(...places.map((p) => p.x));
  const y = Math.min(...places.map((p) => p.y));
  return { x, y, w: Math.max(...places.map((p) => p.x + p.w)) - x, h: Math.max(...places.map((p) => p.y + p.h)) - y };
}

/** A place with whole numbers and sizes of at least 1, as the core keeps it. */
export function rounded(place: BoardPlace): BoardPlace {
  const out: BoardPlace = { x: Math.round(place.x), y: Math.round(place.y), w: Math.max(1, Math.round(place.w)), h: Math.max(1, Math.round(place.h)) };
  const rotate = Math.round(place.rotate ?? 0) % 360;
  if (rotate) out.rotate = rotate;
  if (place.z) out.z = Math.round(place.z);
  return out;
}

/** A place as a patch writes it: `[x, y, w, h, rotate, z]`. */
export function asPatch(place: BoardPlace): number[] {
  const r = rounded(place);
  return [r.x, r.y, r.w, r.h, r.rotate ?? 0, r.z ?? 0];
}

/** A guide line drawn while snapping: vertical at `x`, or horizontal at `y`. */
export type Guide = { x: number } | { y: number };

/**
 * How far to nudge `moving` (the dragged selection's bounds) so one of its edges or its centre
 * lines up with one of `others`' within `threshold`, and the guides to draw.
 */
export function snap(moving: Rect, others: Rect[], threshold: number): { dx: number; dy: number; guides: Guide[] } {
  const xs = (r: Rect) => [r.x, r.x + r.w / 2, r.x + r.w];
  const ys = (r: Rect) => [r.y, r.y + r.h / 2, r.y + r.h];
  /** The smallest nudge lining one of `mine` up with one of `theirs`. */
  const best = (mine: number[], theirs: number[]) => {
    let found: { d: number; at: number } | null = null;
    for (const m of mine) for (const t of theirs) if (Math.abs(t - m) <= threshold && (!found || Math.abs(t - m) < Math.abs(found.d))) found = { d: t - m, at: t };
    return found;
  };
  const bx = best(xs(moving), others.flatMap(xs));
  const by = best(ys(moving), others.flatMap(ys));
  const guides: Guide[] = [];
  if (bx) guides.push({ x: bx.at });
  if (by) guides.push({ y: by.at });
  return { dx: bx?.d ?? 0, dy: by?.d ?? 0, guides };
}

/** How to line items up. */
export type Alignment = "left" | "centre" | "right" | "top" | "middle" | "bottom";

/** The places `places` take when lined up `how` along their bounds. */
export function align(places: Map<string, BoardPlace>, how: Alignment): Map<string, BoardPlace> {
  const box = bounds([...places.values()]);
  const out = new Map<string, BoardPlace>();
  if (!box) return out;
  for (const [id, p] of places) {
    const x = how === "left" ? box.x : how === "centre" ? box.x + (box.w - p.w) / 2 : how === "right" ? box.x + box.w - p.w : p.x;
    const y = how === "top" ? box.y : how === "middle" ? box.y + (box.h - p.h) / 2 : how === "bottom" ? box.y + box.h - p.h : p.y;
    out.set(id, { ...p, x, y });
  }
  return out;
}

/** The places `places` take spread out evenly along `axis`: the outer two stay, the gaps between all become equal. */
export function distribute(places: Map<string, BoardPlace>, axis: "x" | "y"): Map<string, BoardPlace> {
  const size = axis === "x" ? "w" : "h";
  const sorted = [...places].sort(([, a], [, b]) => a[axis] - b[axis]);
  const out = new Map<string, BoardPlace>();
  if (sorted.length < 3) return out;
  const first = sorted[0]![1];
  const last = sorted[sorted.length - 1]![1];
  const room = last[axis] + last[size] - first[axis] - sorted.reduce((sum, [, p]) => sum + p[size], 0);
  const gap = room / (sorted.length - 1);
  let at = first[axis];
  for (const [id, p] of sorted) {
    out.set(id, { ...p, [axis]: at });
    at += p[size] + gap;
  }
  return out;
}

/** Which corner or side of the selection a resize handle holds. */
export type Handle = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";

/** Every handle, clockwise from the top-left corner. */
export const HANDLES: readonly Handle[] = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

/** The smallest an item gets while resized. */
export const MIN_SIZE = 16;

/**
 * The places of `start` (the selection, whose bounds are `box`) once `handle` is dragged by
 * (`dx`, `dy`). `keep`: the proportions `ratio` (width / height; the box's own by default, a
 * picture's natural one) are kept: a corner follows the pointer's bigger change, a side grows
 * the other way around the middle.
 */
export function resized(start: Map<string, BoardPlace>, box: Rect, handle: Handle, dx: number, dy: number, keep = false, ratio = box.w / box.h): Map<string, BoardPlace> {
  let { x, y, w, h } = box;
  if (handle.includes("w")) (x += dx), (w -= dx);
  if (handle.includes("e")) w += dx;
  if (handle.includes("n")) (y += dy), (h -= dy);
  if (handle.includes("s")) h += dy;
  w = Math.max(MIN_SIZE, w);
  h = Math.max(MIN_SIZE, h);
  if (keep && ratio > 0 && Number.isFinite(ratio)) {
    if (handle.length === 2) {
      if (w / box.w >= h / box.h) h = w / ratio;
      else w = h * ratio;
    } else if (handle === "e" || handle === "w") h = w / ratio;
    else w = h * ratio;
    if (h < MIN_SIZE) (h = MIN_SIZE), (w = h * ratio);
    if (w < MIN_SIZE) (w = MIN_SIZE), (h = w / ratio);
    // The side or corner dragged moves; the opposite one stays (a side: the middle stays).
    x = handle.includes("w") ? box.x + box.w - w : handle.includes("e") ? box.x : box.x + (box.w - w) / 2;
    y = handle.includes("n") ? box.y + box.h - h : handle.includes("s") ? box.y : box.y + (box.h - h) / 2;
  } else {
    if (handle.includes("w")) x = box.x + box.w - w;
    if (handle.includes("n")) y = box.y + box.h - h;
  }
  const sx = w / box.w;
  const sy = h / box.h;
  const out = new Map<string, BoardPlace>();
  for (const [id, p] of start) out.set(id, { ...p, x: x + (p.x - box.x) * sx, y: y + (p.y - box.y) * sy, w: Math.max(MIN_SIZE, p.w * sx), h: Math.max(MIN_SIZE, p.h * sy) });
  return out;
}

/** The part of a picture shown (`style.crop`), as fractions of the whole picture. */
export interface Crop {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The part shown that `style.crop` holds (`"x y w h"`, fractions), `null` when none or not one. */
export function parseCrop(value: string | undefined): Crop | null {
  const parts = (value ?? "").trim().split(/\s+/).map(Number);
  if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return null;
  const [x, y, w, h] = parts as [number, number, number, number];
  if (x < 0 || y < 0 || w <= 0 || h <= 0 || x + w > 1.0001 || y + h > 1.0001) return null;
  return { x, y, w, h };
}

/** `crop` as `style.crop` holds it (`""`: the whole picture). */
export function cropText(crop: Crop): string {
  const r = (n: number) => String(Math.round(n * 10000) / 10000);
  if (crop.x <= 0.0001 && crop.y <= 0.0001 && crop.w >= 0.9999 && crop.h >= 0.9999) return "";
  return [crop.x, crop.y, crop.w, crop.h].map(r).join(" ");
}

/** Where the whole picture lies when `place` shows its part `crop` (none: the whole). */
export function wholePicture(place: Rect, crop: Crop | null): Rect {
  if (!crop) return { x: place.x, y: place.y, w: place.w, h: place.h };
  const w = place.w / crop.w;
  const h = place.h / crop.h;
  return { x: place.x - crop.x * w, y: place.y - crop.y * h, w, h };
}

/** The part of the whole picture `whole` that `box` shows. */
export function cropOf(whole: Rect, box: Rect): Crop {
  return { x: (box.x - whole.x) / whole.w, y: (box.y - whole.y) / whole.h, w: box.w / whole.w, h: box.h / whole.h };
}

/**
 * A crop gesture: `handle` moves a side or corner of the part shown (`box`), within the whole
 * picture; `"move"` slides the picture under it, the part shown staying where it is.
 */
export function cropped(whole: Rect, box: Rect, handle: Handle | "move", dx: number, dy: number): { whole: Rect; box: Rect } {
  if (handle === "move") {
    const x = Math.min(box.x, Math.max(box.x + box.w - whole.w, whole.x + dx));
    const y = Math.min(box.y, Math.max(box.y + box.h - whole.h, whole.y + dy));
    return { whole: { ...whole, x, y }, box };
  }
  let [left, top, right, bottom] = [box.x, box.y, box.x + box.w, box.y + box.h];
  if (handle.includes("w")) left = Math.min(right - MIN_SIZE, Math.max(whole.x, left + dx));
  if (handle.includes("e")) right = Math.max(left + MIN_SIZE, Math.min(whole.x + whole.w, right + dx));
  if (handle.includes("n")) top = Math.min(bottom - MIN_SIZE, Math.max(whole.y, top + dy));
  if (handle.includes("s")) bottom = Math.max(top + MIN_SIZE, Math.min(whole.y + whole.h, bottom + dy));
  return { whole, box: { x: left, y: top, w: right - left, h: bottom - top } };
}

/** The ids of the items whose rectangle a marquee `rect` touches (Figma's: no need to go all round). */
export function touched(content: BoardContent, rect: Rect): string[] {
  const r = normal(rect);
  return content.items.filter((i) => i.place.x < r.x + r.w && i.place.x + i.place.w > r.x && i.place.y < r.y + r.h && i.place.y + i.place.h > r.y).map((i) => i.id);
}

/** The angle (degrees, 0 = up) from `centre` to `p`, in steps of 15 when `stepped` (Shift). */
export function angleTo(centre: Point, p: Point, stepped = false): number {
  const degrees = (Math.atan2(p.x - centre.x, centre.y - p.y) * 180) / Math.PI;
  return stepped ? Math.round(degrees / 15) * 15 : Math.round(degrees);
}

/** Where a line from the centre of `place` towards `towards` leaves its rectangle (rotation left out). */
export function edgePoint(place: Rect, towards: Point): Point {
  const cx = place.x + place.w / 2;
  const cy = place.y + place.h / 2;
  const [dx, dy] = [towards.x - cx, towards.y - cy];
  if (!dx && !dy) return { x: cx, y: cy };
  const scale = Math.min(dx ? place.w / 2 / Math.abs(dx) : Infinity, dy ? place.h / 2 / Math.abs(dy) : Infinity);
  return { x: cx + dx * scale, y: cy + dy * scale };
}

/** Whether a frame is a component (`style.component`): a part made once and copied by instances. */
export function isComponent(frame: BoardFrame): boolean {
  return frame.group.style?.component === "yes";
}

/** The screens of a board: its top-level frames that are not components, left to right, then top to bottom. */
export function screensOf(content: BoardContent): BoardFrame[] {
  return content.frames.filter((f) => f.depth === 0 && !isComponent(f)).sort((a, b) => a.place.x - b.place.x || a.place.y - b.place.y);
}

/** The screen under `p` (a top-level frame, not a component), if any. */
export function screenAt(content: BoardContent, p: Point): string | null {
  return screensOf(content).find((f) => contains(f.place, p))?.id ?? null;
}

/** The highest `z` on the board (0 when nothing has one). */
export function topZ(content: BoardContent): number {
  return Math.max(0, ...content.items.map((i) => i.place.z ?? 0));
}

/** The lowest `z` on the board (0 when nothing has one). */
export function bottomZ(content: BoardContent): number {
  return Math.min(0, ...content.items.map((i) => i.place.z ?? 0));
}

/** How many steps a stroke's points are written in, across its box (`style.path`). */
export const INK_STEPS = 1000;

/** The points of a stroke drawn by hand, its box and how it is written (`style.path`). */
export interface Ink {
  /** The box around the stroke, its width added so the line is not cut. */
  place: Rect;
  /** `"x,y x,y …"`: each point in steps of {@link INK_STEPS} across the box. */
  path: string;
}

/** `points` with those within `tolerance` of the line through their neighbours left out (Ramer–Douglas–Peucker). */
export function simplified(points: readonly Point[], tolerance: number): Point[] {
  if (points.length < 3) return [...points];
  const keep = new Array<boolean>(points.length).fill(false);
  keep[0] = keep[points.length - 1] = true;
  const stack: [number, number][] = [[0, points.length - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    const [p, q] = [points[a]!, points[b]!];
    const len = Math.hypot(q.x - p.x, q.y - p.y);
    let far = -1;
    let most = tolerance;
    for (let i = a + 1; i < b; i++) {
      const r = points[i]!;
      const d = len ? Math.abs((q.x - p.x) * (p.y - r.y) - (p.x - r.x) * (q.y - p.y)) / len : Math.hypot(r.x - p.x, r.y - p.y);
      if (d > most) (most = d), (far = i);
    }
    if (far >= 0) {
      keep[far] = true;
      stack.push([a, far], [far, b]);
    }
  }
  return points.filter((_, i) => keep[i]);
}

/** A stroke drawn through `points` (board units) `width` wide: its box and its path, simplified within `tolerance`. */
export function inkOf(points: readonly Point[], width: number, tolerance = 0.5): Ink | null {
  if (!points.length) return null;
  const kept = simplified(points, tolerance);
  const pad = width / 2 + 1;
  const xs = kept.map((p) => p.x);
  const ys = kept.map((p) => p.y);
  const x = Math.floor(Math.min(...xs) - pad);
  const y = Math.floor(Math.min(...ys) - pad);
  const w = Math.max(MIN_SIZE, Math.ceil(Math.max(...xs) + pad - x));
  const h = Math.max(MIN_SIZE, Math.ceil(Math.max(...ys) + pad - y));
  const path = kept.map((p) => `${Math.round(((p.x - x) / w) * INK_STEPS)},${Math.round(((p.y - y) / h) * INK_STEPS)}`).join(" ");
  return { place: { x, y, w, h }, path };
}

/** The points a stroke's `path` holds, in a box `w` × `h` (its own units, from its corner). */
export function inkPoints(path: string | undefined, w: number, h: number): Point[] {
  return (path ?? "").split(/\s+/).flatMap((pair) => {
    const [x, y] = pair.split(",").map(Number);
    return Number.isFinite(x) && Number.isFinite(y) ? [{ x: (x! / INK_STEPS) * w, y: (y! / INK_STEPS) * h }] : [];
  });
}

/** An SVG path through `points`, smoothed (each corner rounded through the middles of its sides). */
export function smoothPath(points: readonly Point[]): string {
  const r = (n: number) => Math.round(n * 10) / 10;
  if (!points.length) return "";
  if (points.length === 1) return `M${r(points[0]!.x)} ${r(points[0]!.y)}h0.1`;
  let d = `M${r(points[0]!.x)} ${r(points[0]!.y)}`;
  for (let i = 1; i < points.length - 1; i++) {
    const [p, q] = [points[i]!, points[i + 1]!];
    d += `Q${r(p.x)} ${r(p.y)} ${r((p.x + q.x) / 2)} ${r((p.y + q.y) / 2)}`;
  }
  const last = points[points.length - 1]!;
  return `${d}L${r(last.x)} ${r(last.y)}`;
}
